import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Droplets, Wind, Shield, Zap, Mountain, Thermometer } from 'lucide-react';
import { ProductCard, TechLabel } from '../components/common/TacticalCard';
import { technologiesApi } from '../lib/api';
import { staggerContainer } from '../lib/animations';

const TechnologyPage = () => {
  const { slug } = useParams();
  const [technology, setTechnology] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const iconMap = {
    Droplets,
    Wind,
    Shield,
    Zap,
    Mountain,
    Thermometer,
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await technologiesApi.getBySlug(slug);
        setTechnology(response.data.technology);
        setProducts(response.data.products);
      } catch (err) {
        console.error('Error:', err);
        // Mock data
        setTechnology({
          tech_id: 'mock',
          name: slug.replace(/-/g, ' ').toUpperCase(),
          description: 'Premium teknoloji ile üstün performans.',
          icon: 'Shield',
          color: '#c4a84b'
        });
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-vav-bg pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-vav-accent-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const Icon = iconMap[technology?.icon] || Shield;

  return (
    <motion.main 
      className="min-h-screen bg-vav-bg pt-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Hero */}
      <div 
        className="relative py-16 overflow-hidden"
        style={{ backgroundColor: `${technology?.color}10` }}
      >
        <div className="absolute inset-0 bg-grid-tactical bg-grid opacity-10" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/shop" 
            className="inline-flex items-center gap-2 text-vav-fg-muted hover:text-vav-accent-cyan mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Mağazaya Dön
          </Link>

          <div className="flex items-center gap-6">
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center"
              style={{ backgroundColor: `${technology?.color}20`, color: technology?.color }}
            >
              <Icon className="w-10 h-10" />
            </div>
            <div>
              <TechLabel variant="accent" className="mb-2">[TEKNOLOJİ]</TechLabel>
              <h1 className="font-heading text-4xl sm:text-5xl text-vav-fg">
                {technology?.name}
              </h1>
              <p className="text-vav-fg-muted mt-2 max-w-xl">
                {technology?.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-heading text-2xl text-vav-fg mb-8">
          {technology?.name} Teknolojisine Sahip Ürünler
        </h2>

        {products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-vav-fg-muted mb-4">Bu teknolojiye sahip ürün bulunamadı</p>
            <Link to="/shop" className="tactical-btn">
              Tüm Ürünleri Gör
            </Link>
          </div>
        ) : (
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="initial"
            animate="enter"
          >
            {products.map((product, index) => (
              <ProductCard key={product.product_id} product={product} index={index} />
            ))}
          </motion.div>
        )}
      </div>
    </motion.main>
  );
};

export default TechnologyPage;
